import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { PaginationProps } from "@insite/mobius/Pagination";
import buttonDisplayProps from "@insite/mobius/Pagination/buttonDisplayProps";
import Select, { SelectComponentProps } from "@insite/mobius/Select";
import { applyPropBuilder, breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

interface OpenOrderPaginationProps {
    totalPages: number;
    currentPage: number;
    onPageChange: any;
    onResultsPerPageChange: any;
}

interface PaginationPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        arrowButtons?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    pageSize: state.context.settings.settingsCollection.websiteSettings.defaultPageSize,
});

type Props = OpenOrderPaginationProps &
    ReturnType<typeof mapStateToProps> &
    PaginationPresentationProps &
    ThemeProps<BaseTheme>;

const responsiveStyles = css`
    margin: 4px 0;
    width: 100%;
    justify-content: center;
    display: flex;
    align-items: center;
`;

const iconButtonStyles = css`
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    width: 24px;
                `,
                null,
                null,
                css`
                    width: 46px;
                `,
                null,
            ],
            "min",
        )};
    justify-content: center;
`;

const numericButtonStyles = css`
    padding: 0 8px;
`;

const PaginationStyle = styled.div<InjectableCss>`
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                null,
                css`
                    flex-wrap: wrap;
                `,
                null,
                null,
            ],
            "max",
        )};
    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    margin: 4px 0;
                `,
                null,
                null,
                css`
                    margin: 16px 0;
                `,
                null,
            ],
            "min",
        )};
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, responsiveStyles, null, null], "max")};
    ul {
        list-style: none;
    }
    li {
        display: inline-block;
    }
    ${injectCss}
`;

const PerPageSelect = styled.span<InjectableCss>`
    ${({ theme }) => breakpointMediaQueries(theme, [null, null, responsiveStyles, null, null], "max")};
    ${injectCss}
`;

const Ellipsis = styled.li<InjectableCss>`
    ${injectCss}
`;

const OpenOrderPagination: FC<Props> = withTheme(props => {
    const { totalPages, mergeCss, currentPage, onPageChange, onResultsPerPageChange, pageSize, ...otherProps } = props;

    const [resultsPerPage, setResultsPerPage] = useState(pageSize);

    const { spreadProps, applyProp, applyStyledProp } = applyPropBuilder(otherProps, { component: "pagination" });

    const cssOverrides = spreadProps("cssOverrides" as any);
    const buttonProps = spreadProps("buttonProps" as any);
    const currentPageButtonVariant = applyProp("currentPageButtonVariant");
    const navIconsSrc = spreadProps("navIconsSrc");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.pagination?.defaultProps?.mergeCss;

    const finalPageIndex = Math.floor((totalPages - 1) / resultsPerPage) + 1;

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            onPageChange(page);
        }
    };

    const generatePageSizeOptions = (baseSize: number, count = 4) => {
        return Array.from({ length: count }, (_, i) => baseSize * (i + 1));
    };

    const handleResultsPerPageChange = (value: number) => {
        setResultsPerPage(value);
        onResultsPerPageChange(value);
        onPageChange(1); // Reset to first page
    };   

    const renderPageNumbers = () => {
        const pages = [];

        if (totalPages <= 7) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(renderPageButton(i));
            }
        } else {
            // Always show first page
            pages.push(renderPageButton(1));

            // Show ellipsis if currentPage > 4
            if (currentPage > 4) {
                pages.push(<Ellipsis key="start-ellipsis">&hellip;</Ellipsis>);
            }

            // Show middle pages
            const startPage = Math.max(2, currentPage - 1);
            const endPage = Math.min(totalPages - 1, currentPage + 1);

            for (let i = startPage; i <= endPage; i++) {
                pages.push(renderPageButton(i));
            }

            // Show ellipsis if currentPage < totalPages - 3
            if (currentPage < totalPages - 3) {
                pages.push(<Ellipsis key="end-ellipsis"> &hellip;</Ellipsis>);
            }

            // Always show last page
            pages.push(renderPageButton(totalPages));
        }

        return pages;
    };


    const renderPageButton = (page: number) => (
        <li key={`page-${page}`}>
            <Button
                {...buttonDisplayProps(props, {
                    moreCss: numericButtonStyles,
                    page,
                    cssOverrides,
                    buttonProps,
                    currentPageButtonVariant,
                })}
                type="button"
                onClick={() => handlePageChange(page)}
            >
                {page.toString()}
            </Button>
        </li>
    );

    const forwardDisabled = currentPage === totalPages || totalPages === 1;
    const backDisabled = currentPage === 1 || totalPages === 1;

    const navIcon = (
        pageIndex: number,
        icon: string | React.ComponentType,
        ariaLabel: string,
        disabledFlag: boolean,
        dataTestSelector?: string,
        onPageChange?: (pageIndex: number) => void,
    ) => (
        <Button
            variant="secondary"
            {...buttonDisplayProps(props, {
                page: -1,
                moreCss: iconButtonStyles,
                cssOverrides,
                buttonProps,
                currentPageButtonVariant,
            })}
            aria-label={ariaLabel}
            disabled={disabledFlag}
            onClick={() => (onPageChange ? onPageChange(pageIndex) : undefined)}
            {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
            type="button"
        >
            <VisuallyHidden>{ariaLabel}</VisuallyHidden>
            <ButtonIcon size={15} src={icon} />
        </Button>
    );

    return (
        <PaginationStyle
            className="pagination-container"
            css={applyStyledProp(["cssOverrides", "pagination"], resolvedMergeCss)}
            {...omitSingle(otherProps, "cssOverrides")}
        >
            {generatePageSizeOptions(pageSize)?.length > 0 && (
                <PerPageSelect css={applyStyledProp(["cssOverrides", "perPageSelect"], resolvedMergeCss)}>
                    <Select
                        value={resultsPerPage}
                        label={translate("Results Per Page")}
                        labelPosition="left"
                        preventEllipsis={true}
                        {...spreadProps("selectProps" as any)}
                        onChange={e => handleResultsPerPageChange(Number(e.target.value))}
                        data-test-selector="openOrdersPagination_resultaPerPage"
                    >
                        {generatePageSizeOptions(pageSize)?.map(num => (
                            <option value={num} key={num}>
                                {num}
                            </option>
                        ))}
                    </Select>
                </PerPageSelect>
            )}
            <LinkList
                role="navigation"
                aria-label={translate("Pagination navigation")}
                css={applyStyledProp(["cssOverrides", "linkList"], resolvedMergeCss)}
                data-test-selector={`pagination_currentPage_${currentPage}`}
            >
                <ul>
                    <li>
                        {navIcon(
                            1,
                            navIconsSrc.firstPage,
                            translate("First page"),
                            backDisabled,
                            "pagination_firstButton",
                            () => onPageChange(1)
                        )}
                    </li>
                    <li>
                        {navIcon(
                            currentPage - 1,
                            navIconsSrc.previousPage,
                            translate("Previous page"),
                            backDisabled,
                            "pagination_previousButton",
                            () => onPageChange(currentPage-1)
                        )}
                    </li>
                    {renderPageNumbers()}
                    <li>
                        {navIcon(
                            currentPage + 1,
                            navIconsSrc.nextPage,
                            translate("Next page"),
                            forwardDisabled,
                            "pagination_nextButton",
                            () => onPageChange(currentPage+1)
                        )}
                    </li>
                    <li>
                        {navIcon(
                            finalPageIndex,
                            navIconsSrc.lastPage,
                            translate("Last page"),
                            forwardDisabled,
                            "pagination_lastButton",
                            () => onPageChange(totalPages)
                        )}
                    </li>
                </ul>
            </LinkList>
        </PaginationStyle>
    );
});
export default connect(mapStateToProps)(withTheme(OpenOrderPagination));
